import { useEffect, useState } from 'react'
import CB from 'lib'
import { useHistory, useLocation } from 'react-router-dom'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { alertSnackbarContentProps } from 'components/AlertSnackbar'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { getClientMockData } from 'utils/clientMockData'

// Define an interface for the mock data item
interface MockDataItem {
    id: string
    type: string
    case_note?: string
    due_date?: string
    creation_date: string
    case_number: string
    case_status: string
    merchant_name: string
    trans_date: string
    amount: string
    currency: string
    account: string
    [key: string]: any // For any other properties
}

interface DocumentInformation {
    case_comments?: string
    link?: string
}

type TDisputeType =
    | 'initiate-purchase-request'
    | 'initiate-merchant-inquiry'
    | 'initiate-split-request'
    | 'raise-chargeback'
    | 'mark-as-resolved'

const useCaseQCReview = () => {
    const history: any = useHistory()
    const location: any = useLocation()
    const [documentInfo, setDocumentInfo] = useState<DocumentInformation>({})
    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false)
    const [uploadModalButtonText, setUploadModalButtonText] = useState(
        'Initiate PreArb'
    )
    const [caseInfo, setCaseInfo] = useState<any>({})
    const [caseId, setCaseId] = useState<number | undefined>(undefined)
    const [alertSnackbarMainOpen, setAlertSnackbarMainOpen] = useState<boolean>(
        false
    )
    const [
        alertSnackbarMainProps,
        setAlertSnackbarMainProps,
    ] = useState<alertSnackbarContentProps>({})
    const [documentLoading, setDocumentLoading] = useState<boolean>(true)
    const [documentLoadKey, setDocumentLoadKey] = useState<number>(0)
    const [loadingCaseInfo, setLoadingCaseInfo] = useState<boolean>(true)
    const [showApproveConfirm, setShowApproveConfirm] = useState<boolean>(false)
    const [errorGettingNewCase, setErrorGettingNewCase] = useState<boolean>(
        false
    )
    const [
        errorGettingNewCaseText,
        setErrorGettingNewCaseText,
    ] = useState<string>('')
    const [refreshCaseInfo, setRefreshCaseInfo] = useState<boolean>(false)
    const [
        isLoadingApproveQCReview,
        setIsLoadingApproveQCReview,
    ] = useState<boolean>(false)

    const [openDisputeModal, setOpenDisputeModal] = useState<boolean>(false)
    const [disputeTypeModal, setDisputeTypeModal] = useState<TDisputeType>(
        'mark-as-resolved'
    )

    const { setSelectedPatchIDs, setSelectedPatchType, user } = useAuthedUser()
    const { themeVariantObj } = useThemeStyling()
    const showClientTypeIssuerUI = user?.is_issuer ?? false

    const showReviewPendingAIType =
        location?.state?.caseType?.toLowerCase() === 'ai doc' &&
        location?.state?.filterTab?.toLowerCase() === 'review_pending'

    const goBackToQCReviewGrid = () => {
        history.push({
            pathname: `/case-qc-review`,
            state: {},
        })
    }
    const handleRedirectRoute = (
        pathname: string,
        state?: { [key: string]: any }
    ) => {
        history.push({
            pathname: pathname,
            state: {
                ...state,
            },
        })
    }

    const getNextQCReviewCase = () => {
        return CB.cases
            .callQCReviewGet()
            .then((data: any) => {
                const nextId = data.data.id
                if (!nextId) {
                    throw new Error('No more cases for QC Review.')
                }

                setCaseId(nextId)

                return CB.cases
                    .callCasePatch(nextId.toString(), 1, 'qc-review')
                    .then(() => {
                        setSelectedPatchIDs && setSelectedPatchIDs(nextId)
                        setSelectedPatchType &&
                            setSelectedPatchType('qc-review')
                        history.push({
                            pathname: `/case-qc-review/view-case/${nextId}`,
                            state: {
                                caseId: nextId,
                            },
                        })
                    })
            })
            .catch((error) => {
                const errorMsg =
                    'An unknown error occurred. Please try again later.'

                setDocumentLoading(false)
                setDocumentInfo({})
                setCaseInfo({})
                setCaseId(undefined)
                setErrorGettingNewCase(true)

                if (error.response?.data?.message)
                    return setErrorGettingNewCaseText(
                        error.response?.data?.message ?? errorMsg
                    )
                if (typeof error.message === 'string')
                    return setErrorGettingNewCaseText(error.message)

                setErrorGettingNewCaseText(errorMsg)
            })
    }

    const approveQCReview = async () => {
        if (!caseId) return
        setIsLoadingApproveQCReview(true)
        CB.cases
            .approveQCReview(caseId)
            .then(() => {
                setAlertSnackbarMainProps({
                    title: 'Success',
                    message: `Case ${caseId} was successfully approved. Loading next...`,
                    intent: 'success',
                })
                setAlertSnackbarMainOpen(true)
                getNextQCReviewCase()
            })
            .catch(() => {
                setAlertSnackbarMainProps({
                    title: 'Error',
                    message: `An error occurred processing this request. Please try again later.`,
                    intent: 'error',
                })
                setAlertSnackbarMainOpen(true)
            })
            .finally(() => {
                setShowApproveConfirm(false)
                setIsLoadingApproveQCReview(false)
            })
    }

    const getDocumentS3URL = (caseId: number) => {
        setDocumentLoading(true)

        CB.documents
            .representment(caseId)
            .then((data) => {
                setDocumentInfo({
                    link: data.link,
                    case_comments: data.case_comments,
                })
            })
            .catch(() => {
                setAlertSnackbarMainProps({
                    title: 'Error',
                    message:
                        'An error occurred retrieving document for this case.',
                    intent: 'error',
                })
                setAlertSnackbarMainOpen(true)
            })
            .finally(() => {
                setDocumentLoadKey(documentLoadKey + 1)
                if (documentLoadKey === 0) {
                    return setDocumentLoading(false)
                }
                setTimeout(() => {
                    setDocumentLoading(false)
                }, 2500)
            })
    }

    const refreshPDFView = () => {
        if (!caseId) return
        getDocumentS3URL(caseId)
    }

    useEffect(() => {
        const stateCaseId = location?.state?.caseId

        if (!stateCaseId) {
            setErrorGettingNewCase(true)
            return setErrorGettingNewCaseText('An unknown error has occurred.')
        }
        setCaseId(stateCaseId)

          if (showClientTypeIssuerUI) {
              let selectedCase: MockDataItem | undefined = getClientMockData(
                  themeVariantObj,
                  'New'
              ).data.find((item: MockDataItem) => {
                  if (location?.state?.caseType?.toLowerCase() === 'ai doc') {
                      return (
                          item.id === stateCaseId.toString() &&
                          item.type?.toLowerCase() === 'ai doc'
                      )
                  }
                  return item.id === stateCaseId.toString()
              })

              // If not found in newMock, try to find in inProgressMock
              if (!selectedCase) {
                  selectedCase = getClientMockData(
                      themeVariantObj,
                      'InProgress'
                  ).data.find((item: MockDataItem) => {
                      if (
                          location?.state?.caseType?.toLowerCase() === 'ai doc'
                      ) {
                          return (
                              item.id === stateCaseId.toString() &&
                              item.type?.toLowerCase() === 'ai doc'
                          )
                      }
                      return item.id === stateCaseId.toString()
                  })
              }

            setCaseInfo(selectedCase)
            setLoadingCaseInfo(false)
            getDocumentS3URL(stateCaseId)
        } else {
            CB.cases
                .callQCReviewGet(stateCaseId)
                .then((data) => {
                    setCaseInfo(data.data)
                    setLoadingCaseInfo(false)
                    getDocumentS3URL(stateCaseId)
                })
                .catch((err) => {
                    setErrorGettingNewCase(true)
                    if (err.response?.data?.message)
                        return setErrorGettingNewCaseText(
                            err.response.data.message
                        )
                    setErrorGettingNewCaseText('An unknown error has occurred.')
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, refreshCaseInfo])

    const handleSubmitMarkResolved = async () => {
        if (!caseId) {
            setAlertSnackbarMainProps({
                title: 'Error',
                message: `An error occurred while marking the case as resolved. Please try again later.`,
                intent: 'error',
            })
            return setAlertSnackbarMainOpen(true)
        }

        handleRedirectRoute('/case-management', {
            enableAlertSnackbar: true,
            alertSnackbarContent: {
                message: 'The case was successfully marked as resolved!',
                title: 'Success',
                intent: 'success',
            },
        })
        setOpenDisputeModal(false)
    }

    const handleSubmitDisputeType = async () => {
        if (!caseId || !disputeTypeModal) {
            setAlertSnackbarMainProps({
                title: 'Error',
                message: `An error occurred while updating the dispute type. Please try again later.`,
                intent: 'error',
            })
            return setAlertSnackbarMainOpen(true)
        }

        handleRedirectRoute('/case-management', {
            enableAlertSnackbar: true,
            alertSnackbarContent: {
                message: 'The dispute type was successfully updated!',
                title: 'Success',
                intent: 'success',
            },
        })
        setOpenDisputeModal(false)
    }

    const handlePostUploadChallengeResponse = () => {
        if (!caseId) {
            setAlertSnackbarMainProps({
                title: 'Error',
                message: `An error occurred while completing the challenge response. Please try again later.`,
                intent: 'error',
            })
            return setAlertSnackbarMainOpen(true)
        }

        handleRedirectRoute('/case-management', {
            enableAlertSnackbar: true,
            alertSnackbarContent: {
                message: 'The challenge response was successfully completed!',
                title: 'Success',
                intent: 'success',
            },
        })
    }

    const disputeModalDetails: Record<
        TDisputeType,
        { title: string; description: string; onSubmit: () => void }
    > = {
        'initiate-purchase-request': {
            title: 'Initiate Purchase Insight ',
            description:
                'Request information about the merchant and transaction.',
            onSubmit: handleSubmitDisputeType,
        },
        'initiate-merchant-inquiry': {
            title: showClientTypeIssuerUI
                ? 'Initiate Split Request'
                : 'Initiate Merchant Request',
            description: 'Request a 50% refund from the merchant to resolve.',
            onSubmit: handleSubmitDisputeType,
        },
        'initiate-split-request': {
            title: 'Initiate Split Request',
            description: 'Request a 50% refund from the merchant to resolve.',
            onSubmit: handleSubmitDisputeType,
        },
        'raise-chargeback': {
            title: 'Initiate Chargeback',
            description:
                'Initiate a formal dispute and raise a chargeback case.',
            onSubmit: handleSubmitDisputeType,
        },
        'mark-as-resolved': {
            title: 'Mark Case as Resolved',
            description:
                'Marking this case as resolved will close it in the system and remove it from your active disputes.',
            onSubmit: handleSubmitMarkResolved,
        },
    }

    return {
        alertSnackbarMainOpen,
        setAlertSnackbarMainOpen,
        alertSnackbarMainProps,
        setAlertSnackbarMainProps,
        documentInfo,
        uploadModalOpen,
        caseInfo: caseInfo ?? {},
        documentLoading,
        documentLoadKey,
        loadingCaseInfo,
        showApproveConfirm,
        errorGettingNewCase,
        errorGettingNewCaseText,
        refreshPDFView,
        isLoadingApproveQCReview,
        approveQCReview,
        getNextQCReviewCase,
        setUploadModalOpen,
        uploadModalButtonText,
        setUploadModalButtonText,
        goBackToQCReviewGrid,
        setShowApproveConfirm,
        setRefreshCaseInfo,
        showReviewPendingAIType,
        handleSubmitMarkResolved,
        openDisputeModal,
        setOpenDisputeModal,
        disputeModalDetails,
        disputeTypeModal,
        setDisputeTypeModal,
        handleSubmitDisputeType,
        handlePostUploadChallengeResponse,
    }
}

export default useCaseQCReview
